import { useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import apiUrls from "../../../api";
import type { AgentStatus, AgentInfo } from "../hooks/useAgentConnection";
import { uninstallAgent, openFolderViaAgent } from "../utils/agentInstaller";
import AgentUninstallButton from "./AgentUninstallButton";
import { Spinner, DownloadArrowIcon, ComputerIcon, FolderOpenIcon, ChevronIcon } from "./icons";

interface Props {
  status: AgentStatus;
  agentInfo: AgentInfo | null;
  onRetry: () => void;
}

function detectClientPlatform(): "darwin" | "win32" {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes("mac")) return "darwin";
  return "win32";
}

const AgentConnectionBar = ({ status, agentInfo, onRetry }: Props) => {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const platform = detectClientPlatform();
      const res = await axios.get(apiUrls.getDesktopAgentDownloadUrl, {
        params: { platform },
      });
      // Use a hidden anchor with download attribute to avoid opening a blank tab
      const a = document.createElement("a");
      a.href = res.data.url;
      a.download = "";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      // Fallback: open without signing (will likely 403 but at least shows intent)
    } finally {
      setDownloading(false);
    }
  };

  if (status === "detecting") {
    return (
      <div className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200">
        <Spinner className="w-4 h-4 text-green-500" />
        <span className="text-sm text-gray-500">{t("desktopAgent.detecting")}</span>
      </div>
    );
  }

  if (status === "unavailable") {
    return (
      <div className="p-6 bg-white rounded-lg border border-gray-200">
        <div className="flex flex-col items-center text-center gap-4">
          <ComputerIcon className="w-12 h-12 text-gray-300" />
          <div>
            <p className="text-sm font-medium text-gray-700">{t("desktopAgent.notDetected")}</p>
            <p className="text-xs text-gray-400 mt-1 max-w-sm">
              {t("desktopAgent.notDetectedHint")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm text-white bg-green-500 hover:bg-green-600 transition-colors disabled:opacity-50"
            >
              {downloading ? <Spinner className="w-4 h-4" /> : <DownloadArrowIcon />}
              {t("desktopAgent.download")}
            </button>
            <button
              onClick={onRetry}
              className="text-xs text-gray-400 hover:text-gray-600 px-3 py-2 rounded-md border border-gray-200 transition-colors"
            >
              {t("desktopAgent.retry")}
            </button>
          </div>
          <div className="text-xs text-gray-400 mt-1 space-y-1">
            <p>{t("desktopAgent.step1")}</p>
            <p>{t("desktopAgent.step2")}</p>
            <p>{t("desktopAgent.step3")}</p>
          </div>
        </div>
      </div>
    );
  }

  // Connected
  const osLabel =
    agentInfo?.os.platform === "darwin"
      ? "macOS"
      : agentInfo?.os.platform === "win32"
      ? "Windows"
      : agentInfo?.os.platform || "";

  return (
    <div className="p-4 bg-white rounded-lg border border-gray-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-gray-700">{t("desktopAgent.connected")}</p>
            <p className="text-xs text-gray-400 mt-0.5">
              v{agentInfo?.agentVersion} &middot; {osLabel}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-green-500 font-medium">
            {agentInfo?.installedApps.length ?? 0} {t("desktopAgent.appsInstalled")}
          </span>

          <AgentUninstallButton
            onUninstall={() => uninstallAgent()}
            label={t("desktopAgent.uninstallAgent")}
          />
        </div>
      </div>
      <p className="text-xs text-gray-400 mt-3">{t("desktopAgent.description")}</p>

      {/* Collapsible info section */}
      {agentInfo && agentInfo.installedApps.length > 0 && (
        <div className="mt-3 border-t border-gray-100 pt-2">
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ChevronIcon className="w-3 h-3" open={showInfo} />
            {t("desktopAgent.installInfo")}
          </button>

          {showInfo && (
            <div className="mt-2 space-y-1.5">
              {agentInfo.installedApps.map((app) => (
                <div
                  key={app.id}
                  className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 rounded-md"
                >
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-600 truncate">{app.displayName}</p>
                    <p className="text-[11px] text-gray-400 truncate font-mono">
                      {app.installPath}
                    </p>
                  </div>
                  <button
                    onClick={() => openFolderViaAgent(app.id)}
                    title={t("desktopAgent.openFolder")}
                    className="flex items-center gap-1 shrink-0 text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded border border-gray-200 hover:bg-gray-100 transition-colors"
                  >
                    <FolderOpenIcon className="w-3.5 h-3.5" />
                    {t("desktopAgent.openFolder")}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentConnectionBar;
